import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useAccounts } from '../lib/useAccounts';
import { usd } from '../lib/clientDeepData';
import { loadRecentQuotes, osQuoteEngineUrl, type RecentQuote } from '../lib/osData';

// Maps the canonical OS quote statuses (STATE_MACHINES.quotes: draft, approval,
// ready, sent, won, lost, rejected, archived) to the CRM status-tag CSS classes.
// Legacy/alias values (accepted/approved/viewed/expired) are tolerated so old
// records still colour correctly.
const stTag = (s: string) => {
  switch (s) {
    case 'won':
    case 'accepted':
    case 'approved':
      return 'st-completed';
    case 'ready':
    case 'sent':
    case 'viewed':
    case 'approval':
      return 'st-in_progress';
    case 'lost':
    case 'rejected':
    case 'expired':
      return 'st-blocked';
    default: // draft, archived, unknown
      return 'st-new';
  }
};

export default function QuoteEngine() {
  const { demo } = useAuth();
  const { data } = useAccounts(demo);
  const [params] = useSearchParams();
  const [recent, setRecent] = useState<RecentQuote[]>([]);
  const [live, setLive] = useState(false);
  const [loading, setLoading] = useState(!demo);
  const [q, setQ] = useState('');

  // optional client context (?id=) so the New Quote button links the customer
  const acct = params.get('id') ? (data?.accounts ?? []).find((a) => a.id === params.get('id')) : undefined;
  const newQuoteUrl = osQuoteEngineUrl(acct ? { customer: acct.name, customerId: acct.id.replace(/^cust_/, '') } : {});

  useEffect(() => {
    if (demo) { setLoading(false); return; }
    loadRecentQuotes(60).then((r) => { setRecent(r); setLive(r.length > 0); }).catch(() => {}).finally(() => setLoading(false));
  }, [demo]);

  const rows = useMemo(() => {
    if (!q.trim()) return recent;
    const t = q.toLowerCase();
    return recent.filter((r) => r.quoteNum.toLowerCase().includes(t) || r.customerName.toLowerCase().includes(t) || (r.sku || '').toLowerCase().includes(t));
  }, [recent, q]);

  return (
    <>
      <div className="page-head">
        <div><div className="page-title">Quote Engine</div><div className="page-sub">Opens the live OS quote engine. New quotes save to the shared {`quotes`} collection and appear on the client profile.</div></div>
        {live ? <span className="live-flag">Live · OS quotes</span> : <span className="demo-flag">Demo data</span>}
      </div>

      {/* ONE button → new quote in the exact OS engine */}
      <div className="newquote-hero">
        <div className="ehex">QT</div>
        <h2 className="engine-hero-title">{acct ? `New quote for ${acct.name}` : 'Start a new quote'}</h2>
        <p className="engine-hero-sub">Specs, Materials, Pricing, Matrix, Portal, PO, Art, SO — the exact OS engine on shared data &amp; Drive.</p>
        <a className="btn primary newquote-btn" href={newQuoteUrl} target="_blank" rel="noreferrer">＋ New Quote</a>
      </div>

      {/* live registry (reopen existing) */}
      <div className="card" style={{ marginTop: 18 }}>
        <div className="card-header">
          <div><h3>Open a Quote</h3><p>{live ? `${recent.length} live quotes from the OS` : 'Sign in to the OS to load live quotes'}</p></div>
          <input className="search" style={{ maxWidth: 260 }} placeholder="Search quote, client, SKU…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="table-wrap"><table>
          <thead><tr><th>Quote</th><th>Customer</th><th>SKU</th><th>Total</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {loading && <tr><td colSpan={6}><div className="empty">Loading live quotes…</div></td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={6}><div className="empty">{demo ? 'Demo mode — sign into the OS to load live quotes.' : 'No quotes found.'}</div></td></tr>}
            {rows.map((r) => (
              <tr key={r.id} onClick={() => window.open(osQuoteEngineUrl({ quoteId: r.id }), '_blank', 'noopener')}>
                <td><strong>{r.quoteNum}</strong></td><td>{r.customerName}</td><td style={{ color: 'var(--tx2)' }}>{r.sku || '—'}</td>
                <td>{usd(r.total)}</td><td><span className={`tag ${stTag(r.status)}`}>{r.status}</span></td>
                <td style={{ textAlign: 'right' }}><span className="btn small">Open ↗</span></td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>
    </>
  );
}
